import { clientEnvModule } from "@/modules/client-env";
import { uuidModule } from "@/modules/uuid";
import { ATTACHMENT_PAGE_PROCESSING_LIMIT } from "@/store/note-attachments/constants";
import { resolveSpaceAccountNoteAttachmentSyncModelUuid } from "@/store/note-attachments/resolveAttachmentUuid";
import {
  NoteAttachmentProcessingStatus,
  ProcessingInfo,
  SpaceAccountNoteAttachmentModelData,
} from "@/store/note-attachments/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import {
  type BatchCreateSpaceAccountNoteAttachmentItem,
  type IBatchCreateSpaceAccountNoteAttachmentOperation,
} from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncModelKind } from "@/store/sync/types";

const PENDING: NoteAttachmentProcessingStatus = "PENDING";

export class BatchCreateSpaceAccountNoteAttachmentOperation extends BaseSyncOperation<IBatchCreateSpaceAccountNoteAttachmentOperation> {
  get operationKind() {
    return "BATCH_CREATE_SPACE_ACCOUNT_NOTE_ATTACHMENT" as const;
  }

  /**
   * The BE serializer accepts `attachment_url`, `mime_type`, `page_count` as
   * optional upload hints (used to build the optimistic stub server-side when
   * no `NoteAttachment` row exists yet — see MEM-16898). `filename` is FE-only;
   * the backend silently drops it at the validation boundary.
   */
  public async generateSyncOperation(): Promise<IBatchCreateSpaceAccountNoteAttachmentOperation> {
    return {
      id: this.operationId,
      client_id: await clientEnvModule.clientId(),
      locally_committed_at: this.committedAt,
      operation_kind: this.operationKind,
      payload: this.payload,
    };
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    return this.payload.note_attachments.map(item => this.buildOptimisticUpdate(item));
  }

  /**
   * `BaseSyncModelStore` writes optimistic updates to the queue table, but the
   * model's local table is only populated by `recompute(modelId)`. Without
   * this hook the optimistic stub stays invisible to `localTable` consumers
   * (e.g. `getContentUnderstandingForNote`) until the BE round-trip lands and
   * triggers a remote-driven recompute — the badge would only appear after
   * the BE response.
   */
  async triggerRecompute(): Promise<void> {
    for (const item of this.payload.note_attachments) {
      const modelId = resolveSpaceAccountNoteAttachmentSyncModelUuid(
        item.associated_note_attachment_id
      );
      await this.store.spaceAccountNoteAttachments.recompute(modelId);
    }
  }

  private buildOptimisticUpdate(
    item: BatchCreateSpaceAccountNoteAttachmentItem
  ): OptimisticSyncUpdate<SpaceAccountNoteAttachmentModelData> {
    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: resolveSpaceAccountNoteAttachmentSyncModelUuid(
          item.associated_note_attachment_id
        ),
        model_kind: SyncModelKind.SpaceAccountNoteAttachment,
        model_version: 1,
        model_data: buildOptimisticModelData(item, this.committedAt),
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };
  }
}

const buildOptimisticModelData = (
  item: BatchCreateSpaceAccountNoteAttachmentItem,
  committedAt: string
): SpaceAccountNoteAttachmentModelData => {
  const isImage = item.mime_type.startsWith("image/");
  return {
    associated_note_attachment_id: item.associated_note_attachment_id,
    associated_note_id: item.associated_note_id,
    created_at: committedAt,
    attachment_url: item.attachment_url,
    // FE-written hosting_status is set on the create item for direct uploads
    // (PENDING) so the optimistic row reflects it immediately; absent ⇒ null
    // ("never attempted"), matching legacy. READY/FAILED transitions come later.
    serving_url: null,
    hosting_status: item.hosting_status ?? null,
    hosting_failure_reason: null,
    mime_type: item.mime_type,
    filename: item.filename,
    page_count: isImage ? 1 : item.page_count,
    size_bytes: null,
    extraction_status: PENDING,
    indexing_status: PENDING,
    processing_info: buildProcessingInfo(item, isImage),
    summary: null,
    suggested_search: null,
    ocr_markdown: null,
  };
};

const buildProcessingInfo = (
  item: BatchCreateSpaceAccountNoteAttachmentItem,
  isImage: boolean
): ProcessingInfo => {
  if (isImage) {
    return {
      pages_processed: 1,
      pages_total: 1,
      pages_failed: 0,
      was_cutoff: false,
      cutoff_limit: null,
    };
  }

  if (item.page_count === null) {
    return {
      pages_processed: null,
      pages_total: null,
      pages_failed: 0,
      was_cutoff: false,
      cutoff_limit: ATTACHMENT_PAGE_PROCESSING_LIMIT,
    };
  }

  return {
    pages_processed: Math.min(item.page_count, ATTACHMENT_PAGE_PROCESSING_LIMIT),
    pages_total: item.page_count,
    pages_failed: 0,
    was_cutoff: item.page_count > ATTACHMENT_PAGE_PROCESSING_LIMIT,
    cutoff_limit: ATTACHMENT_PAGE_PROCESSING_LIMIT,
  };
};
